import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Navbar, Nav, Container, Offcanvas, Button, Collapse } from 'react-bootstrap';
import { useAuth } from '../../contexts/AuthContext';
import './Layout.css';

interface MenuItem {
  path?: string;
  label: string;
  icon: string;
  children?: MenuItem[];
}

const Layout: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showSidebar, setShowSidebar] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const menuItems: MenuItem[] = [
    { path: '/dashboard', label: '儀表板', icon: 'fas fa-tachometer-alt' },
    {
      label: '基本資料',
      icon: 'fas fa-building',
      children: [
        { path: '/parking-management', label: '停車場管理', icon: 'fas fa-car' },
        { path: '/locations', label: '地點設定', icon: 'fas fa-map-marker-alt' },
        { path: '/fee-categories', label: '收費類別', icon: 'fas fa-tags' },
        { path: '/vehicle-types', label: '車輛類型', icon: 'fas fa-truck' },
        { path: '/fee-settings', label: '收費設定', icon: 'fas fa-dollar-sign' },
        { path: '/parking', label: '車位管理', icon: 'fas fa-car' },
        { path: '/lane-management', label: '車道管理', icon: 'fas fa-road' },
      ]
    },
    {
      label: '車牌辨識',
      icon: 'fas fa-id-card',
      children: [
        { path: '/plate-recognition', label: '車牌辨識鏡頭', icon: 'fas fa-video' },
        { path: '/lane-management', label: '閘道門管理', icon: 'fas fa-door-open' },
        { path: '/ai-monitoring', label: '即時影像監控', icon: 'fas fa-eye' },
        { path: '/circular-track', label: '環形軌道', icon: 'fas fa-route' },
      ]
    },
    {
      label: '車輛管理',
      icon: 'fas fa-list-alt',
      children: [
        { path: '/vehicle-registration', label: '車輛登記', icon: 'fas fa-clipboard-list' },
        { path: '/etag-management', label: 'e-Tag 管理', icon: 'fas fa-tags' },
        { path: '/blacklist-management', label: '黑名單管理', icon: 'fas fa-ban' },
      ]
    },
    {
      label: '即時查詢',
      icon: 'fas fa-search',
      children: [
        { path: '/entry-exit-records', label: '進出記錄', icon: 'fas fa-history' },
        { path: '/onsite-vehicles', label: '在場車輛', icon: 'fas fa-car-side' }
      ]
    },
    { path: '/vip-management', label: 'VIP管理', icon: 'fas fa-crown' },
    {
      label: '費用管理',
      icon: 'fas fa-coins',
      children: [
        { path: '/temporary-payment-query', label: '臨停車繳費查詢', icon: 'fas fa-file-invoice-dollar' },
        { path: '/monthly-payment-query', label: '月租車繳費查詢', icon: 'fas fa-calendar-check' },
        { path: '/payments', label: '支付管理', icon: 'fas fa-credit-card' },
        { path: '/billing', label: '收費管理', icon: 'fas fa-receipt' },
      ]
    },
    { path: '/reports', label: '報表分析', icon: 'fas fa-chart-bar' },
    { path: '/ai-monitoring', label: 'AI監控', icon: 'fas fa-shield-alt' },
    { path: '/environment-control', label: '環境控制', icon: 'fas fa-leaf' },
    { path: '/system-admin', label: '系統管理', icon: 'fas fa-cog' },
  ];

  const toggleMenu = (label: string) => {
    setExpandedMenus(prev => 
      prev.includes(label) 
        ? prev.filter(item => item !== label)
        : [...prev, label]
    );
  };

  const isMenuExpanded = (label: string) => expandedMenus.includes(label);

  const renderMenuItem = (item: MenuItem, isMobile: boolean = false) => {
    if (item.children) {
      return (
        <div key={item.label} className={isMobile ? "mobile-menu-item" : "menu-item"}>
          <div
            className={`sidebar-item d-flex align-items-center justify-content-between ${isMobile ? 'mobile-nav-link' : ''}`}
            onClick={() => toggleMenu(item.label)}
            style={{ cursor: 'pointer' }}
          >
            <div className="d-flex align-items-center">
              <i className={`${item.icon} me-3`}></i>
              <span>{item.label}</span>
            </div>
            <i className={`fas fa-chevron-${isMenuExpanded(item.label) ? 'down' : 'right'} ms-2`}></i>
          </div>
          <Collapse in={isMenuExpanded(item.label)}>
            <div className="submenu">
              {item.children.map(child => (
                <div
                  key={child.path}
                  className={`sidebar-item submenu-item d-flex align-items-center ${
                    location.pathname === child.path ? 'active' : ''
                  } ${isMobile ? 'mobile-nav-link' : ''}`}
                  onClick={() => {
                    navigate(child.path!);
                    if (isMobile) setShowSidebar(false);
                  }}
                  style={{ cursor: 'pointer', paddingLeft: '3rem' }}
                >
                  <i className={`${child.icon} me-3`}></i>
                  <span>{child.label}</span>
                </div>
              ))}
            </div>
          </Collapse>
        </div>
      );
    } else {
      return (
        <div
          key={item.path}
          className={`sidebar-item d-flex align-items-center ${
            location.pathname === item.path ? 'active' : ''
          } ${isMobile ? 'mobile-nav-link' : ''}`}
          onClick={() => {
            navigate(item.path!);
            if (isMobile) setShowSidebar(false);
          }}
          style={{ cursor: 'pointer' }}
        >
          <i className={`${item.icon} me-3`}></i>
          <span>{item.label}</span>
        </div>
      );
    }
  };

  const isActivePath = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <div className="layout">
      {/* 頂部導航欄 */}
      <Navbar bg="dark" variant="dark" expand="lg" className="navbar-custom">
        <Container fluid>
          <Button
            variant="outline-light"
            className="me-3 d-lg-none"
            onClick={() => setShowSidebar(true)}
          >
            <i className="fas fa-bars"></i>
          </Button>
          
          <Navbar.Brand href="/dashboard" className="d-flex align-items-center">
            <i className="fas fa-parking me-2"></i>
            震亞物聯 停車管理系統
          </Navbar.Brand>

          <Nav className="ms-auto">
            <Nav.Link className="d-flex align-items-center text-light">
              <i className="fas fa-user me-2"></i>
              {user?.username}
            </Nav.Link>
            <Nav.Link onClick={handleLogout} className="text-light">
              <i className="fas fa-sign-out-alt me-2"></i>
              登出
            </Nav.Link>
          </Nav>
        </Container>
      </Navbar>

      <div className="layout-content">
        {/* Desktop Sidebar */}
        <div className="sidebar d-none d-lg-block">
          <div className="sidebar-nav">
            {menuItems.map(item => renderMenuItem(item))}
          </div>
        </div>

        {/* 移動版側邊欄 */}
        <Offcanvas
          show={showSidebar}
          onHide={() => setShowSidebar(false)}
          placement="start"
          className="d-lg-none"
        >
          <Offcanvas.Header closeButton>
            <Offcanvas.Title>
              <i className="fas fa-parking me-2"></i>
              停車管理系統
            </Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body>
            <div className="sidebar-nav">
              {menuItems.map(item => renderMenuItem(item, true))}
            </div>
          </Offcanvas.Body>
        </Offcanvas>

        {/* 主要內容區域 */}
        <main className="main-content">
          <Container fluid className="py-4 h-100">
            <Outlet />
          </Container>
        </main>
      </div>
    </div>
  );
};

export default Layout;